"use client"

import { useRef, useState, useEffect } from "react"
import { Button } from "@/components/ui/button"
import { Loader2, X, Paperclip, UploadCloud } from "lucide-react"
import { useClientRequestsApi } from "@/hooks/use-client-requests-api"
import type { ChangeRequestAttachmentDto } from "@/hooks/use-client-requests-api"

const MAX_FILE_SIZE_BYTES = 10 * 1024 * 1024 // 10MB
const MAX_FILES = 5

interface UploadedFile {
  localId: string
  attachment: ChangeRequestAttachmentDto
}

interface PendingFile {
  localId: string
  name: string
  isUploading: boolean
  error?: string
}

interface FileUploadProps {
  onFilesChange: (attachments: ChangeRequestAttachmentDto[]) => void
  onUploadingChange: (isUploading: boolean) => void
  disabled?: boolean
}

export function FileUpload({ onFilesChange, onUploadingChange, disabled = false }: FileUploadProps) {
  const { uploadFile } = useClientRequestsApi()
  const fileInputRef = useRef<HTMLInputElement>(null)
  const [uploadedFiles, setUploadedFiles] = useState<UploadedFile[]>([])
  const [pendingFiles, setPendingFiles] = useState<PendingFile[]>([])

  useEffect(() => {
    onFilesChange(uploadedFiles.map((f) => f.attachment))
  }, [uploadedFiles, onFilesChange])

  useEffect(() => {
    onUploadingChange(pendingFiles.some((p) => p.isUploading))
  }, [pendingFiles, onUploadingChange])

  const totalFiles = uploadedFiles.length + pendingFiles.filter((p) => p.isUploading).length
  const canAddMore = totalFiles < MAX_FILES

  const handleFileSelect = async (files: FileList | null) => {
    if (!files) return

    const filesToAdd = Array.from(files).slice(0, Math.max(0, MAX_FILES - totalFiles))

    for (const file of filesToAdd) {
      if (file.size > MAX_FILE_SIZE_BYTES) {
        const localId = crypto.randomUUID()
        setPendingFiles((prev) => [
          ...prev,
          { localId, name: file.name, isUploading: false, error: "File exceeds 10MB limit" },
        ])
        continue
      }

      const localId = crypto.randomUUID()
      setPendingFiles((prev) => [...prev, { localId, name: file.name, isUploading: true }])

      try {
        const response = await uploadFile(file)

        const attachment: ChangeRequestAttachmentDto = {
          name: response.name,
          fileStorageId: response.fileStorageId,
          extension: response.extension,
          size: response.size,
        }

        setUploadedFiles((prev) => [...prev, { localId, attachment }])
        setPendingFiles((prev) => prev.filter((p) => p.localId !== localId))
      } catch (error) {
        setPendingFiles((prev) =>
          prev.map((p) =>
            p.localId === localId
              ? { ...p, isUploading: false, error: error instanceof Error ? error.message : "Upload failed" }
              : p
          )
        )
      } finally {
        setPendingFiles((prev) =>
          prev.map((p) => (p.localId === localId && p.isUploading ? { ...p, isUploading: false } : p))
        )
      }
    }

    if (fileInputRef.current) {
      fileInputRef.current.value = ""
    }
  }

  const handleRemoveUploaded = (localId: string) => {
    setUploadedFiles((prev) => prev.filter((f) => f.localId !== localId))
  }

  const handleRemovePending = (localId: string) => {
    setPendingFiles((prev) => prev.filter((p) => p.localId !== localId))
  }

  const formatSize = (bytes: string | number) => {
    const n = typeof bytes === "string" ? parseInt(bytes, 10) : bytes
    if (isNaN(n)) return bytes as string
    if (n < 1024) return `${n} B`
    if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`
    return `${(n / (1024 * 1024)).toFixed(1)} MB`
  }

  return (
    <div className="space-y-3">
      {/* Drop zone / upload button */}
      {canAddMore && (
        <button
          type="button"
          className={`w-full border-2 border-dashed rounded-lg p-4 text-center transition-colors bg-transparent ${
            disabled ? "opacity-50 cursor-not-allowed border-gray-200" : "border-gray-300 hover:border-blue-400 cursor-pointer"
          }`}
          disabled={disabled}
          onClick={() => fileInputRef.current?.click()}
        >
          <UploadCloud className="mx-auto h-8 w-8 text-gray-400 mb-2" />
          <p className="text-sm text-gray-600">
            Click to upload files{" "}
            <span className="text-xs text-gray-400">(max {MAX_FILES} files, 10MB each)</span>
          </p>
          <input
            ref={fileInputRef}
            type="file"
            multiple
            className="hidden"
            disabled={disabled}
            onChange={(e) => handleFileSelect(e.target.files)}
          />
        </button>
      )}

      {/* Uploaded files list */}
      {uploadedFiles.length > 0 && (
        <ul className="space-y-2">
          {uploadedFiles.map(({ localId, attachment }) => (
            <li
              key={localId}
              className="flex items-center justify-between gap-2 rounded-md border border-gray-200 bg-gray-50 px-3 py-2 text-sm"
            >
              <div className="flex items-center gap-2 min-w-0">
                <Paperclip className="h-4 w-4 text-gray-400 flex-shrink-0" />
                <span className="truncate text-gray-800">{attachment.name}</span>
                <span className="text-xs text-gray-500 flex-shrink-0">{formatSize(attachment.size)}</span>
              </div>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                className="h-6 w-6 p-0 text-gray-400 hover:text-red-500"
                onClick={() => handleRemoveUploaded(localId)}
                disabled={disabled}
              >
                <X className="h-4 w-4" />
              </Button>
            </li>
          ))}
        </ul>
      )}

      {/* Pending / error files */}
      {pendingFiles.length > 0 && (
        <ul className="space-y-2">
          {pendingFiles.map((pending) => (
            <li
              key={pending.localId}
              className={`flex items-center justify-between gap-2 rounded-md border px-3 py-2 text-sm ${
                pending.error ? "border-red-300 bg-red-50" : "border-gray-200 bg-gray-50"
              }`}
            >
              <div className="flex items-center gap-2 min-w-0">
                {pending.isUploading ? (
                  <Loader2 className="h-4 w-4 animate-spin text-blue-500 flex-shrink-0" />
                ) : (
                  <Paperclip className="h-4 w-4 text-gray-400 flex-shrink-0" />
                )}
                <span className="truncate text-gray-800">{pending.name}</span>
                {pending.isUploading && (
                  <span className="text-xs text-blue-500 flex-shrink-0">Uploading...</span>
                )}
                {pending.error && (
                  <span className="text-xs text-red-500 flex-shrink-0">{pending.error}</span>
                )}
              </div>
              {!pending.isUploading && (
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  className="h-6 w-6 p-0 text-gray-400 hover:text-red-500"
                  onClick={() => handleRemovePending(pending.localId)}
                  disabled={disabled}
                >
                  <X className="h-4 w-4" />
                </Button>
              )}
            </li>
          ))}
        </ul>
      )}
    </div>
  )
}
